import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import cn from 'classnames';
import { TrendingUp, Receipt, Calculator } from 'lucide-react';
import { formatCurrency } from '@utils/helpers/currency';
import { SupportedCurrency } from '@utils/types/organization/organization';
import { CurrencyTotals } from '@utils/types/back-office/usage-revenue';

interface SummaryCardsProps {
  totalUsage: number;
  totalBilled: number;
  forecast: number;
  isLoading?: boolean;
  className?: string;
  currency?: SupportedCurrency;
  // Money per invoice currency. When customers do not share one there is no
  // single figure to show — there is no exchange rate here — so each currency
  // is listed on its own line rather than being added together.
  byCurrency?: CurrencyTotals[];
}

/* eslint-disable no-restricted-syntax -- Semantic colors for KPI card icons */
const KPI_TINTS = {
  usage: { color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)' },
  billed: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' },
  forecast: { color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.1)' }
};
/* eslint-enable no-restricted-syntax */

export default function SummaryCards({
  totalUsage,
  totalBilled,
  forecast,
  isLoading = false,
  className,
  currency = 'EUR',
  byCurrency
}: SummaryCardsProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  // One currency (or an older response without the breakdown) keeps the single
  // figure it always showed. Several means listing them; usage is untouched
  // either way, since units are not denominated in anything.
  const split = byCurrency && byCurrency.length > 1 ? byCurrency : null;

  const money = (
    flat: number,
    pick: (entry: CurrencyTotals) => number
  ): string[] =>
    split
      ? split.map(entry => formatCurrency(pick(entry), entry.currency))
      : [formatCurrency(flat, byCurrency?.[0]?.currency ?? currency)];

  const cards = [
    {
      label: t('total-usage'),
      value: [`${totalUsage.toLocaleString()} ${t('units')}`],
      icon: TrendingUp,
      tint: KPI_TINTS.usage
    },
    {
      label: t('total-billed'),
      value: money(totalBilled, entry => entry.total_billed),
      icon: Receipt,
      tint: KPI_TINTS.billed
    },
    {
      label: t('forecast-eom'),
      value: money(forecast, entry => entry.forecast),
      icon: Calculator,
      tint: KPI_TINTS.forecast
    }
  ];

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-3 gap-4', className)}>
      {cards.map((card, index) => {
        const Icon = card.icon;
        return (
          <div
            key={index}
            className="rounded-2xl border border-hairline bg-app-soft shadow-card hover:shadow-card-hover transition-shadow p-4"
          >
            <div className="flex items-start justify-between">
              <div>
                <p
                  className="text-xs font-medium mb-1"
                  style={{ color: getColour('inactiveText') }}
                >
                  {card.label}
                </p>
                {isLoading ? (
                  <div
                    className="h-7 w-24 rounded-2xl animate-pulse"
                    style={{ backgroundColor: getColour('thirdLayer') }}
                  />
                ) : (
                  <div
                    className="text-xl font-bold"
                    style={{ color: getColour('normalText') }}
                  >
                    {card.value.map(line => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
              <div
                className="p-2 rounded-full"
                style={{ backgroundColor: card.tint.bg }}
              >
                <Icon className="h-5 w-5" style={{ color: card.tint.color }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
